import { useCallback, useMemo, useState } from 'react';

import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { commitUpdateContentMode } from '../mutations/commitUpdateContentMode.ts';
import {
	flipContentMode,
	resolveContentStatus,
	uiToServerContentMode,
	type UiContentMode,
} from '../utils/contentMode.ts';

import { useEditorContentModeValue } from './useEditorContentModeValue.ts';

/**
 * Reads content mode via the read-only hook, toggles via mutation,
 * and uses a small local override to bridge unpublished drafts.
 *
 * Mode can be undefined when the page is autoconverted and the contentMode from server is null.
 */
export function useEditorContentMode({ contentId }: { contentId: string }): {
	mode: UiContentMode;
	isSaving: boolean;
	toggle: () => Promise<{ ok: boolean; from: UiContentMode; to: UiContentMode }>;
} {
	const { cloudId } = useSessionData();
	const isLivePage = useIsCurrentPageLive();
	const isEditorPage = useIsEditorPage();
	const contentStatus = resolveContentStatus({ isLivePage, isEditorPage });
	const { mode = 'standard' } = useEditorContentModeValue({ contentId });

	const [isSaving, setIsSaving] = useState(false);

	const toggle = useCallback(async () => {
		if (isSaving) {
			return { ok: false, from: mode, to: mode };
		}

		const from = mode;
		const to = flipContentMode(from);

		setIsSaving(true);
		try {
			const res = await commitUpdateContentMode({
				contentId,
				contentMode: uiToServerContentMode(to),
				contentStatus,
				refetchContentId: contentId,
				cloudId,
			});

			const payload = res?.data?.confluence_updateContentMode;
			const errorCount = payload?.errors?.length ?? 0;
			const ok = payload?.success !== false && errorCount <= 0;
			return { ok: Boolean(ok), from, to };
		} catch (err) {
			markErrorAsHandled(err);
			return { ok: false, from, to };
		} finally {
			setIsSaving(false);
		}
	}, [isSaving, mode, contentId, contentStatus, cloudId]);

	return useMemo(() => ({ mode, isSaving, toggle }), [mode, isSaving, toggle]);
}
